/**
 * Oferta de uma etapa do funil (/oferta/:offerId, docs/OFERTAS.md): quem tem
 * oportunidade aberta na etapa diz se tem interesse (e vai para o pagamento)
 * ou por que não tem. Pelo QR projetado (na sala ou na aula online) a pessoa
 * se identifica pelo WhatsApp; o link pessoal (`?t=`) já sabe quem é.
 */

export type OfferStatus = 'ok' | 'expired' | 'closed' | 'invalid' | 'not_found'
export type OfferInterest = 'interested' | 'not_interested'

export interface OfferContext {
  status: OfferStatus
  class_name?: string | null
  offer_id?: string
  title?: string
  description?: string | null
  price_label?: string | null
  has_checkout?: boolean
  objections?: string[]
  expires_at?: string
  first_name?: string | null
  identified?: boolean
  interest?: OfferInterest
  payment?: OfferPayment | null
  /** Volta no "Tenho interesse": pré-preenche os dados de quem paga. */
  buyer?: { name?: string | null; email?: string | null; phone?: string | null } | null
}

/** Condições de pagamento da oferta; null quando ela só registra interesse. */
export interface OfferPayment {
  product_name: string
  /** O produto libera o curso sozinho; sem isso, a equipe cuida da entrega. */
  grants_access?: boolean
  price: number
  original_price: number | null
  installments: number
  installment_amount: number
  /** Total no cartão parcelado; acima do `price` quando o parcelado tem juros. */
  card_total?: number
  label: string
  /** O que cada número de parcelas cobra (1 = à vista). */
  options?: Array<{ installments: number; total: number; amount: number }>
}

export type OrderStatus = 'pending' | 'processing' | 'paid' | 'failed' | 'expired' | 'canceled' | 'refunded' | 'chargedback'

export interface OfferOrder {
  id: string
  amount: number
  product_name: string
  payment_method: 'pix' | 'credit_card' | null
  payment_status: OrderStatus
  fulfilled: boolean
}

export interface OfferPix {
  qr_code: string | null
  qr_code_text: string
  expires_at: string | null
}

/** Recusa da oferta ao criar o pedido ou pagar (vem como `data.status`). */
export const OFFER_CHECKOUT_REFUSALS: Record<string, string> = {
  expired: 'O prazo desta oferta acabou.',
  closed: 'Esta oferta foi encerrada.',
  not_found: 'Esta oferta não vale para você. Fale com a equipe SLAC.',
  invalid: 'Este pagamento não foi encontrado. Leia o QR de novo.',
  not_for_sale: 'Esta oferta não está à venda por aqui. A equipe SLAC vai falar com você.',
  already_has_access: 'Você já tem acesso a este curso. É só entrar no painel do aluno.',
  contact_consultant: 'Para esta compra, fale com o seu consultor SLAC.',
}

export const OFFER_API = {
  offer: (offerId: string, token?: string | null) =>
    `/api/v2/offers/${encodeURIComponent(offerId)}${token ? `?t=${encodeURIComponent(token)}` : ''}`,
  respond: (offerId: string) => `/api/v2/offers/${encodeURIComponent(offerId)}/respond`,
  checkout: (offerId: string) => `/api/v2/offers/${encodeURIComponent(offerId)}/checkout`,
  order: (offerId: string, orderId: string, action = '') =>
    `/api/v2/offers/${encodeURIComponent(offerId)}/orders/${encodeURIComponent(orderId)}${action ? `/${action}` : ''}`,
}

export function identifyOfferPhone(international: string): string | null {
  const digits = international.replace(/\D/g, '')
  if (digits.startsWith('55')) {
    const national = digits.slice(2)
    return national.length >= 10 ? national : null
  }
  return digits.length >= 8 ? `+${digits}` : null
}

const PHONE_STORAGE_KEY = 'slac:oferta:whatsapp'

export function rememberedOfferPhone(): string {
  try { return window.localStorage.getItem(PHONE_STORAGE_KEY) || '' } catch { return '' }
}

export function rememberOfferPhone(phone: string): void {
  try { window.localStorage.setItem(PHONE_STORAGE_KEY, phone) } catch { /* private browsing */ }
}

export function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

/** CPF (11) ou CNPJ (14) só com dígitos. */
export function isValidDocument(document: string): boolean {
  return [11, 14].includes(document.replace(/\D/g, '').length)
}

/** ViaCEP preenche o endereço de cobrança a partir do CEP. */
export async function lookupZipCode(zip: string): Promise<{ street: string; neighborhood: string; city: string; state: string } | null> {
  const digits = zip.replace(/\D/g, '')
  if (digits.length !== 8) return null

  try {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
    const data = await response.json()
    if (!response.ok || data?.erro) return null
    return { street: data.logradouro ?? '', neighborhood: data.bairro ?? '', city: data.localidade ?? '', state: data.uf ?? '' }
  } catch {
    return null
  }
}

/** Parâmetros dos eventos de pixel: a oferta é o "produto" visto/escolhido. */
export function offerTrackingParams(context: OfferContext): Record<string, string> {
  return {
    content_name: context.title ?? '',
    content_ids: context.offer_id ?? '',
    content_category: 'oferta',
  }
}

/** "Válida até 08/11 às 23:59" no fuso do aparelho. */
export function expiresLabel(iso: string | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `Válida até ${day} às ${time}`
}
